const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const date = new Date(dateStr)
    return Number.isNaN(date.getTime())
        ? dateStr
        : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatAmount = (amount, currency) => {
    if (amount == null || amount === '') return '—'
    const num = Number(amount)
    const formatted = Number.isNaN(num)
        ? amount
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    if (currency === 'USD') return `$${formatted}`
    if (currency === 'ZIG') return `ZIG ${formatted}`
    return formatted
}

const getClientName = (client) => {
    if (!client) return '—'
    if (typeof client === 'string') return client
    const name = [client.first_name, client.last_name].filter(Boolean).join(' ')
    if (name) return name
    if (client.company_details?.name) return client.company_details.name
    return '—'
}

const getClientCompanyName = (client) => {
    if (!client) return '—'
    if (typeof client === 'string') return client
    if (client.display_name) return client.display_name
    if (client.company_details?.display_name) return client.company_details.display_name
    return '—'
}

const DOCUMENT_VARIANTS = {
    invoice: {
        title: 'INVOICE',
        numberLabel: 'Invoice Number:',
        dateLabel: 'Invoice Date:',
        expiryLabel: 'Due Date:',
        termsLabel: 'Payment Instructions',
        printFailMessage: 'Failed to generate invoice PDF',
    },
    quotation: {
        title: 'QUOTATION',
        numberLabel: 'Quotation Number:',
        dateLabel: 'Quotation Date:',
        expiryLabel: 'Valid Until:',
        termsLabel: 'Terms',
        printFailMessage: 'Failed to generate quotation PDF',
    },
}

/**
 * Opens the shared invoice/quotation print template in a new window.
 * Quotations reuse the invoice layout; only labels and field mapping differ.
 */
const resolveLogoUrl = (logo) => {
    if (!logo) return ''
    if (
        logo.startsWith('data:')
        || logo.startsWith('blob:')
        || logo.startsWith('http://')
        || logo.startsWith('https://')
    ) {
        return logo
    }
    const path = logo.startsWith('/') ? logo : `/${logo}`
    if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin}${path}`
    }
    return path
}

export const generateDocumentPDF = async (document, { variant = 'invoice', company } = {}) => {
    const labels = DOCUMENT_VARIANTS[variant] || DOCUMENT_VARIANTS.invoice

    try {
        const printWindow = window.open('', '_blank', 'width=860,height=1000')
        if (!printWindow) {
            alert('Please allow popups to generate PDF')
            return
        }

        const client = document.client_details || {}
        const clientName = getClientName(document.client_name || client)
        const clientCompany = getClientCompanyName(client.display_name ? client : client)
        const lineItems = document.line_items || []
        const issuer = {
            ...(company || {}),
            ...(document.issuer || {}),
        }
        const companyName = (issuer.name || '').trim()
        // For quotations prefer the logo stored on the document itself
        const rawLogo = variant === 'quotation'
            ? (document.company_logo || issuer.logo || '')
            : (issuer.logo || '')
        const logoUrl = resolveLogoUrl(rawLogo)

        const taxAmount = document.tax_amount != null ? Number(document.tax_amount) : null
        const taxRate = document.tax_rate != null ? Number(document.tax_rate) : null
        const docNumber = document.quotation_number || document.invoice_number || ''
        const docDate = document.quotation_date || document.invoice_date
        const expiryDate = document.valid_until || document.due_date
        const terms = document.terms || document.payment_instructions

        printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>${labels.title} ${docNumber}</title>
                    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
                    <style>
                        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
                        body {
                            font-family: 'Inter', sans-serif;
                            color: #2d3748;
                            background: #fff;
                            padding: 48px 56px;
                            font-size: 13px;
                            line-height: 1.6;
                        }

                        /* ── TOP HEADER ── */
                        .header {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            padding-bottom: 28px;
                            border-bottom: 1.5px solid #e2e8f0;
                            margin-bottom: 32px;
                        }
                        .header-left .logo {
                            max-height: 64px;
                            max-width: 200px;
                            object-fit: contain;
                            display: block;
                            margin-bottom: 8px;
                        }
                        .header-left .company-name {
                            font-size: 18px;
                            font-weight: 700;
                            color: #1a202c;
                        }
                        .header-right {
                            text-align: right;
                        }
                        .doc-title {
                            font-size: 38px;
                            font-weight: 800;
                            letter-spacing: 3px;
                            color: #1a202c;
                            line-height: 1;
                            margin-bottom: 4px;
                        }
                        .doc-subtitle {
                            font-size: 11px;
                            color: #718096;
                            letter-spacing: 1px;
                            text-transform: uppercase;
                            margin-bottom: 14px;
                        }
                        .company-info {
                            font-size: 12px;
                            color: #4a5568;
                            line-height: 1.7;
                        }
                        .company-info strong {
                            display: block;
                            font-weight: 600;
                            color: #1a202c;
                            margin-bottom: 2px;
                        }

                        /* ── BILL TO + DOCUMENT META ── */
                        .meta-section {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            margin-bottom: 36px;
                            gap: 40px;
                        }
                        .bill-to {
                            flex: 1;
                        }
                        .section-label {
                            font-size: 10px;
                            font-weight: 700;
                            letter-spacing: 1.5px;
                            text-transform: uppercase;
                            color: #a0aec0;
                            margin-bottom: 8px;
                        }
                        .client-name {
                            font-size: 15px;
                            font-weight: 700;
                            color: #1a202c;
                            margin-bottom: 4px;
                        }
                        .client-detail {
                            color: #4a5568;
                            font-size: 12.5px;
                            line-height: 1.75;
                        }

                        .invoice-meta-box {
                            min-width: 280px;
                            background: #f7fafc;
                            border: 1px solid #e2e8f0;
                            border-radius: 8px;
                            overflow: hidden;
                        }
                        .meta-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 9px 16px;
                            border-bottom: 1px solid #e2e8f0;
                            font-size: 12.5px;
                        }
                        .meta-row:last-child { border-bottom: none; }
                        .meta-row .meta-label { color: #718096; font-weight: 500; }
                        .meta-row .meta-value { font-weight: 600; color: #1a202c; }
                        .meta-row.grand-total-row {
                            background: #fff;
                        }
                        .meta-row.grand-total-row .meta-label { font-weight: 700; color: #1a202c; }
                        .meta-row.grand-total-row .meta-value {
                            font-size: 14px;
                            font-weight: 800;
                            color: #2b6cb0;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 0;
                        }
                        thead tr {
                            background-color: #1a3c5e;
                            color: #fff;
                        }
                        thead th {
                            padding: 11px 14px;
                            font-size: 12px;
                            font-weight: 600;
                            letter-spacing: 0.5px;
                            text-transform: uppercase;
                        }
                        thead th:not(:first-child) { text-align: right; }
                        tbody tr {
                            border-bottom: 1px solid #edf2f7;
                        }
                        tbody tr:last-child { border-bottom: none; }
                        tbody tr:nth-child(even) { background: #f7fafc; }
                        tbody td {
                            padding: 12px 14px;
                            font-size: 13px;
                            color: #2d3748;
                            vertical-align: top;
                        }
                        tbody td:not(:first-child) { text-align: right; }
                        .item-name { font-weight: 600; color: #1a202c; margin-bottom: 2px; }
                        .item-desc { font-size: 11.5px; color: #718096; line-height: 1.5; }

                        .totals-wrapper {
                            display: flex;
                            justify-content: flex-end;
                            margin-top: 8px;
                        }
                        .totals-box {
                            min-width: 300px;
                        }
                        .totals-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 8px 0;
                            border-bottom: 1px solid #e2e8f0;
                            font-size: 13px;
                        }
                        .totals-row:last-child {
                            border-bottom: none;
                            padding-top: 12px;
                        }
                        .totals-row .t-label { color: #4a5568; }
                        .totals-row .t-value { font-weight: 600; color: #1a202c; }
                        .totals-row.grand {
                            font-size: 14px;
                        }
                        .totals-row.grand .t-label { font-weight: 700; color: #1a202c; }
                        .totals-row.grand .t-value { font-weight: 800; color: #2b6cb0; font-size: 15px; }

                        .notes-section {
                            margin-top: 40px;
                            padding-top: 20px;
                            border-top: 1px solid #e2e8f0;
                        }
                        .notes-section + .notes-section {
                            border-top: none;
                            padding-top: 0;
                            margin-top: 16px;
                        }

                        .footer {
                            text-align: center;
                            margin-top: 60px;
                            padding-top: 24px;
                            border-top: 1px solid #e2e8f0;
                        }
                        .footer .thank-you {
                            font-size: 13px;
                            color: #718096;
                            margin-bottom: 6px;
                        }
                        .footer .powered {
                            font-size: 13px;
                            color: #2d3748;
                            font-weight: 600;
                        }
                        .footer .powered span {
                            color: #2b6cb0;
                        }

                        @media print {
                            body { padding: 24px 32px; }
                            .no-print { display: none !important; }
                        }
                    </style>
                </head>
                <body>

                    <div class="header">
                        <div class="header-left">
                            ${logoUrl
                                ? `<img src="${logoUrl}" class="logo" alt="Company Logo" />`
                                : (variant !== 'quotation' ? `<div class="company-name">${companyName || 'Our Company'}</div>` : '')
                            }
                            ${variant !== 'quotation' && logoUrl && companyName ? `<div class="company-name" style="margin-top:4px;">${companyName}</div>` : ''}
                            ${variant !== 'quotation' && company?.tagline ? `<div style="font-size:11px;color:#718096;margin-top:2px;">${company.tagline}</div>` : ''}
                        </div>
                        <div class="header-right">
                            <div class="doc-title">${labels.title}</div>
                            ${company?.tagline ? `<div class="doc-subtitle">${company.tagline}</div>` : ''}
                            <div class="company-info">
                                <strong>${company?.name || ''}</strong>
                                ${company?.address ? company.address.split(',').map((l) => `<span>${l.trim()}</span><br>`).join('') : ''}
                                ${company?.phone ? `<span>Phone: ${company.phone}</span><br>` : ''}
                                ${company?.website ? `<span>${company.website}</span>` : (company?.email ? `<span>${company.email}</span>` : '')}
                            </div>
                        </div>
                    </div>

                    <div class="meta-section">
                        <div class="bill-to">
                            <div class="section-label">Bill To</div>
                            <div class="client-name">${clientCompany}</div>
                            <div class="client-detail">
                                ${clientName ? `${clientName}<br>` : ''}
                                ${[client.address_line1, client.address_line2].filter(Boolean).join('<br>') || ''}
                                ${client.address_line1 || client.address_line2 ? '<br>' : ''}
                                ${[client.city, client.state, client.country].filter(Boolean).join(', ')}
                                ${client.city || client.country ? '<br>' : ''}
                                ${client.phone ? `${client.phone}<br>` : ''}
                                ${client.email ? `${client.email}` : ''}
                            </div>
                        </div>

                        <div class="invoice-meta-box">
                            <div class="meta-row">
                                <span class="meta-label">${labels.numberLabel}</span>
                                <span class="meta-value">${docNumber}</span>
                            </div>
                            <div class="meta-row">
                                <span class="meta-label">${labels.dateLabel}</span>
                                <span class="meta-value">${formatDate(docDate)}</span>
                            </div>
                            <div class="meta-row">
                                <span class="meta-label">${labels.expiryLabel}</span>
                                <span class="meta-value">${formatDate(expiryDate)}</span>
                            </div>
                            <div class="meta-row grand-total-row">
                                <span class="meta-label">Grand Total (${document.currency || 'USD'}):</span>
                                <span class="meta-value">${formatAmount(document.grand_total, document.currency)}</span>
                            </div>
                        </div>
                    </div>

                    <table>
                        <thead>
                            <tr>
                                <th>Items</th>
                                <th style="text-align:right;">Quantity</th>
                                <th style="text-align:right;">Price</th>
                                <th style="text-align:right;">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${lineItems.map((item) => `
                                <tr>
                                    <td>
                                        <div class="item-name">${item.name || '—'}</div>
                                        ${item.description ? `<div class="item-desc">${item.description}</div>` : ''}
                                    </td>
                                    <td>${item.quantity}</td>
                                    <td>${formatAmount(item.unit_price, document.currency)}</td>
                                    <td>${formatAmount(item.line_total != null ? item.line_total : (item.quantity * item.unit_price), document.currency)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="totals-wrapper">
                        <div class="totals-box">
                            <div class="totals-row">
                                <span class="t-label">Subtotal:</span>
                                <span class="t-value">${formatAmount(document.subtotal, document.currency)}</span>
                            </div>
                            ${taxAmount != null && !Number.isNaN(taxAmount) ? `
                            <div class="totals-row">
                                <span class="t-label">${(taxRate != null && !Number.isNaN(taxRate)) ? ('VAT ' + taxRate + '%:') : 'Tax:'}</span>
                                <span class="t-value">${formatAmount(taxAmount, document.currency)}</span>
                            </div>
                            ` : ''}
                            <div class="totals-row grand">
                                <span class="t-label">Grand Total (${document.currency || 'USD'}):</span>
                                <span class="t-value">${formatAmount(document.grand_total, document.currency)}</span>
                            </div>
                        </div>
                    </div>

                    ${terms ? `
                        <div class="notes-section">
                            <div class="section-label">${labels.termsLabel}</div>
                            <p style="font-size:12.5px;color:#4a5568;margin-top:4px;">${terms}</p>
                        </div>
                    ` : ''}

                    ${document.notes ? `
                        <div class="notes-section">
                            <div class="section-label">Notes</div>
                            <p style="font-size:12.5px;color:#4a5568;margin-top:4px;">${document.notes}</p>
                        </div>
                    ` : ''}

                    <div class="footer">
                        <p class="thank-you">Thank You !!</p>
                        <p class="powered">Powered by <span>${company?.name || 'Our Company'}</span></p>
                    </div>

                    <script>
                        window.onload = function () {
                            window.print();
                        }
                    </script>
                </body>
                </html>
            `)
        printWindow.document.close()
    } catch (err) {
        console.error(err)
        alert(labels.printFailMessage)
    }
}

export const fetchCompanyAndGeneratePDF = async (api, document, variant = 'invoice') => {
    try {
        const companyRes = await api.get('/auth/company/')
        await generateDocumentPDF(document, { variant, company: companyRes.data })
    } catch (err) {
        console.error(err)
        const labels = DOCUMENT_VARIANTS[variant] || DOCUMENT_VARIANTS.invoice
        alert(labels.printFailMessage)
    }
}
